import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Card, CardContent } from '../ui/card'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import {
  Leaf,
  Filter,
  ArrowRight,
  MapPin,
  Calendar,
  DollarSign,
  Eye,
} from 'lucide-react'
import ImagePlaceholder from '../../utils/ImagePlaceholder'
import { ImageWithFallback } from '../../utils/ImageWithFallback'
import service1 from '../../assets/service/service-1.jpg'
import service2 from '../../assets/service/service-2.jpg'
import service3 from '../../assets/service/service-3.jpg'
import service4 from '../../assets/service/service-4.jpg'
import service5 from '../../assets/service/service-5.jpg'
import service6 from '../../assets/service/service-6.jpg'

export function PortfolioComponent2() {
  const [activeFilter, setActiveFilter] = useState('all')
  const [selectedProject, setSelectedProject] = useState(null)
  console.log('selectedProject', selectedProject);

  const filters = [
    { id: 'all', label: 'All Projects' },
    { id: 'residential', label: 'Residential' },
    { id: 'commercial', label: 'Commercial' },
    { id: 'garden', label: 'Garden Design' },
    { id: 'hardscape', label: 'Hardscaping' },
    { id: 'maintenance', label: 'Maintenance' },
  ]
  const projects = [
    {
      id: 1,
      title: "Modern Suburban Transformation",
      category: "residential",
      description: "Complete front and backyard makeover with contemporary design elements.",
      location: "Beverly Hills, CA",
      duration: "6 weeks",
      budget: "$45,000",
      beforeImage: service6,
      afterImage: service6,
      gallery: [
        "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=800&h=600&fit=crop&q=80",
        "https://images.unsplash.com/photo-1519378058457-4c29a0a2efac?w=800&h=600&fit=crop&q=80"
      ],
      features: ["Native plant garden", "Irrigation system", "Outdoor lighting", "Stone pathways"],
      tags: ["Modern", "Eco-friendly", "Low maintenance"]
    },
    {
      id: 2,
      title: "Corporate Headquarters Landscape",
      category: "commercial",
      description: "Professional landscaping for a tech company's new headquarters.",
      location: "Silicon Valley, CA",
      duration: "8 weeks",
      budget: "$85,000",
      beforeImage: service1,
      afterImage: service1,
      gallery: [
        service1
      ],
      features: ["Green roof system", "Rain gardens", "Employee outdoor spaces", "Sustainable design"],
      tags: ["Commercial", "Sustainable", "LEED certified"]
    },
    {
      id: 3,
      title: "English Cottage Garden",
      category: "garden",
      description: "Charming cottage-style garden with heirloom plants and natural materials.",
      location: "Pasadena, CA",
      duration: "4 weeks",
      budget: "$28,000",
      beforeImage: service3,
      afterImage: service3,
      gallery: [
        service3
      ],
      features: ["Perennial borders", "Herb garden", "Water feature", "Rustic fencing"],
      tags: ["Traditional", "Colorful", "Fragrant"]
    },
    {
      id: 4,
      title: "Luxury Pool & Patio Design",
      category: "hardscape",
      description: "Elegant outdoor living space with pool, patio, and outdoor kitchen.",
      location: "Malibu, CA",
      duration: "12 weeks",
      budget: "$125,000",
      beforeImage: service2,
      afterImage: service2,
      gallery: [
        service2
      ],
      features: ["Natural stone patio", "Outdoor kitchen", "Fire pit", "Landscape lighting"],
      tags: ["Luxury", "Entertainment", "High-end"]
    },
    {
      id: 5,
      title: "Community Park Renovation",
      category: "commercial",
      description: "Complete renovation of a neighborhood park with sustainable practices.",
      location: "Santa Monica, CA",
      duration: "16 weeks",
      budget: "$200,000",
      beforeImage: service4,
      afterImage: service4,
      gallery: [
        service4
      ],
      features: ["Playground area", "Walking paths", "Native landscaping", "Sustainable irrigation"],
      tags: ["Public", "Community", "Sustainable"]
    },
    {
      id: 6,
      title: "Rooftop Garden Oasis",
      category: "garden",
      description: "Urban rooftop transformation into a green sanctuary with city views.",
      location: "Downtown LA, CA",
      duration: "5 weeks",
      budget: "$35,000",
      beforeImage: service5,
      afterImage: service5,
      gallery: [
        service5
      ],
      features: ["Container gardens", "Vertical planters", "Seating areas", "Wind-resistant plants"],
      tags: ["Urban", "Rooftop", "Creative"]
    }
  ];


  const filteredProjects =
    activeFilter === 'all'
      ? projects
      : projects.filter((project) => project.category === activeFilter)

  return (
    <section id="portfolio" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <Badge className="mb-4 bg-green-100 text-green-800 border-green-200">
            <Leaf className="w-4 h-4 mr-2" />
            Our Portfolio
          </Badge>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Transformations That Inspire
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Explore our gallery of completed projects and see how we've helped transform
            ordinary outdoor spaces into extraordinary landscapes.
          </p>
        </motion.div>

        {/* Filter Buttons */}
        <motion.div
          className="flex flex-wrap justify-center gap-4 mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          {filters.map((filter) => (
            <Button
              key={filter.id}
              variant={activeFilter === filter.id ? 'default' : 'outline'}
              onClick={() => setActiveFilter(filter.id)}
              className={`${activeFilter === filter.id
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                } transition-all`}
            >
              <Filter className="w-4 h-4 mr-2" />
              {filter.label}
            </Button>
          ))}
        </motion.div>

        {/* Projects Grid */}
        <motion.div layout className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <AnimatePresence mode="wait">
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="group cursor-pointer"
                onClick={() => setSelectedProject(project.id)}
              >
                <Card className="overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-300 group-hover:border-green-200">
                  {/* Project Image */}
                  <div className="relative h-64 overflow-hidden">
                    <ImageWithFallback
                      src={project.afterImage}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    {/* <ImagePlaceholder width={384} height={256} /> */}

                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                      <motion.div
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                        whileHover={{ scale: 1.1 }}
                      >
                        <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-green-600">
                          <Eye className="w-6 h-6" />
                        </div>
                      </motion.div>
                    </div>

                    {/* Project Category */}
                    <div className="absolute top-4 left-4">
                      <Badge className="bg-white/90 text-gray-800 capitalize">
                        {project.category}
                      </Badge>
                    </div>
                  </div>

                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-green-600 transition-colors">
                      {project.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                      {project.description}
                    </p>

                    {/* Project Info */}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-sm text-gray-500">
                        <MapPin className="w-4 h-4 mr-2 flex-shrink-0" />
                        {project.location}
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar className="w-4 h-4 mr-2 flex-shrink-0" />
                        {project.duration}
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <DollarSign className="w-4 h-4 mr-2 flex-shrink-0" />
                        {project.budget}
                      </div>
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {project.tags.slice(0, 3).map((tag, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full group-hover:bg-green-600 group-hover:text-white group-hover:border-green-600 transition-all"
                    >
                      View Details
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {/* Project Modal/Lightbox would go here */}
        {/* For brevity, I'm not implementing the full modal, but it would show project details */}

        {/* CTA Section */}
        <motion.div
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Ready to Start Your Transformation?
            </h3>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Every great landscape starts with a conversation. Let's discuss your vision
              and create something beautiful together.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={() =>
                  document
                    .getElementById('contact')
                    ?.scrollIntoView({ behavior: 'smooth' })
                }
              >
                Get Your Free Consultation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() =>
                  document
                    .getElementById('contact')
                    ?.scrollIntoView({ behavior: 'smooth' })
                }
              >
                Request a Quote
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
